import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { getUnreviewedTasksInGroup } from '@kaiizen/kai-pro-api';
import { useScanEditingContextSlice, useScanNotesMutators } from '@kaiizen/kai-pro-api-react-state';
import {
  getGroupsInStandardGroupOrder,
  isGroupShownOnScopeReport,
  isTaskShownOnScopeReport,
  ScanGroupOptimized,
} from '@kaiizen/kai-scan-template-system';

import { ANALYTICS_EVENTS, trackEvent } from '../analytics';
import { logError } from '../Lib/logger';
import { groupNamePresenter } from '../Presenters';
import { useConfirmationModal } from './ConfirmationModalContext';
import { useNavigateToProject } from './kaiNavigation';
import { useToast } from './ToastContext';
import { useScopeApprovalAction } from './useScopeApprovalAction';

export type ScanReviewPageDataInput = {
  filterRegex?: RegExp;
};

/**
 * Data subscriptions and derived state for the scan review page.
 */
export const useScanReviewPageData = ({ filterRegex }: ScanReviewPageDataInput) => {
  const {
    scanRoot,
    scan,
    scanMutators,
    baselineScanRoot,
    scanIsEditable,
    scanNotesByScanPartId,
  } = useScanEditingContextSlice(['scanRoot', 'scan', 'scanMutators', 'baselineScanRoot', 'scanIsEditable', 'scanNotesByScanPartId']);
  const scanId = scan?.id;
  const scanLifecycleStatus = scan?.scanLifecycleStatus;
  const { showToast } = useToast();
  const scanNoteMutators = useScanNotesMutators(scanId);
  const { scopeApprovalStatus, approveAndPublish } = useScopeApprovalAction();
  const reviewMode = scanLifecycleStatus === 'pendingReview' || scanLifecycleStatus === 'inReview';
  const changesRequestedMode = scanLifecycleStatus === 'correctionsRequested';
  const navigate = useNavigate();
  const navigateToProject = useNavigateToProject();

  const scopedGroupIds = useMemo(
    () => scanRoot?.groups?.filter(isGroupShownOnScopeReport).map(group => group.id) ?? [],
    [scanRoot],
  );

  const scopedGroups = useMemo(
    () => (!scanRoot ? undefined : scopedGroupIds?.map(id => scanRoot.groupsById[id]) ?? []),
    [scanRoot, scopedGroupIds],
  );

  const scopedTaskIdsByGroupId = useMemo(() => {
    const scopedTaskIdsByGroupId: Record<string, string[]> = {};
    scopedGroups?.forEach(group => {
      scopedTaskIdsByGroupId[group.id] = group.tasks?.filter(isTaskShownOnScopeReport).map(task => task.id) ?? [];
    });
    return scopedTaskIdsByGroupId;
  }, [scopedGroups]);

  const confirm = useConfirmationModal();
  const [isBulkApproving, setIsBulkApproving] = useState(false);

  const getUnreviewedTasksInGroupCallback = useCallback(
    (group: ScanGroupOptimized) => {
      if (!scanId) return [];
      return getUnreviewedTasksInGroup(group, scanId, scanNotesByScanPartId, baselineScanRoot, scanRoot);
    },
    [scanId, scanNotesByScanPartId, baselineScanRoot, scanRoot],
  );

  const handleBulkApproveGroup = useCallback(
    async (group: ScanGroupOptimized) => {
      if (!scan || isBulkApproving) return;

      const unreviewedTasks = getUnreviewedTasksInGroupCallback(group);
      const taskCount = unreviewedTasks.length;

      confirm({
        title: 'Approve All Tasks',
        message: `Are you sure you want to approve all ${taskCount} remaining task${taskCount !== 1 ? 's' : ''} in "${groupNamePresenter(group)}"?`,
        confirmLabel: 'Yes, Approve All',
        cancelLabel: 'No, Cancel',
        onConfirm: async () => {
          setIsBulkApproving(true);
          try {
            if (scan.scanLifecycleStatus === 'pendingReview') {
              await scanMutators.update(scan.id, { scanLifecycleStatus: 'inReview' });
            }

            for (const task of unreviewedTasks) {
              await scanNoteMutators.create({ scanPartId: task.id, scanNoteType: 'approval', body: '' });
            }

            trackEvent(ANALYTICS_EVENTS.scanApproved, { scanId: scanId, groupId: group.id, tasksApproved: taskCount, bulkApproval: true });
            showToast(`Approved ${taskCount} task${taskCount !== 1 ? 's' : ''} in ${groupNamePresenter(group)}`, 'success');
          } catch (error) {
            logError('Failed to approve tasks', error, { scanId: scanId, groupId: group.id });
            showToast('Failed to approve tasks', 'error');
          } finally {
            setIsBulkApproving(false);
          }
        },
      });
    },
    [confirm, isBulkApproving, getUnreviewedTasksInGroupCallback, scanNoteMutators, scanId, showToast, scan, scanMutators],
  );

  const submitForReview = useCallback(() => {
    if (!scan) return;
    scanMutators.update(scan.id, {
      scanLifecycleStatus: 'pendingReview',
    });
    trackEvent(ANALYTICS_EVENTS.scanSubmittedForReview, {
      scanId: scan.id,
    });
  }, [scan, scanMutators]);

  const startReview = useCallback(() => {
    if (!scan) return;
    scanMutators.update(scan.id, {
      scanLifecycleStatus: 'inReview',
    });
  }, [scan, scanMutators]);

  const approveScope = useCallback(
    () =>
      confirm({
        title: 'Approve Scope',
        message: 'Complete review and approve this scope?',
        confirmLabel: 'Yes, Approve',
        cancelLabel: 'No, Cancel',
        onConfirm: approveAndPublish,
      }),
    [confirm, approveAndPublish],
  );

  const requestChanges = useCallback(
    () =>
      confirm({
        title: 'Request Changes',
        message: 'Complete review and send requested changes to the field agent?',
        confirmLabel: 'Yes, Request Changes',
        cancelLabel: 'No, Cancel',
        onConfirm: () => {
          if (!scan) return;
          scanMutators.update(scan.id, { scanLifecycleStatus: 'correctionsRequested' });
          trackEvent(ANALYTICS_EVENTS.scanChangesRequested, { scanId: scan.id });
        },
      }),
    [confirm, scan, scanMutators],
  );

  const handlePrintPreview = useCallback(() => {
    if (!scan) return;
    if (scan.scanType === 'changeRequest') {
      navigate(`/scan/${scan.id}/change-order`);
    } else {
      navigate(`/scan/${scan.id}/scope`);
    }
  }, [navigate, scan]);

  const { clusterOrder, groupClusters } = useMemo(() => {
    if (!scanRoot?.template || !scopedGroups) {
      return { clusterOrder: [] as string[], groupClusters: {} as Record<string, ScanGroupOptimized[]> };
    }
    return getGroupsInStandardGroupOrder(
      scanRoot.template,
      scopedGroups,
      group => isGroupShownOnScopeReport(group) && (!filterRegex || filterRegex.test(group.optimized.searchText)),
    );
  }, [scanRoot?.template, scopedGroups, filterRegex]);

  const isReady = !!(scopeApprovalStatus && scanId && scanRoot && scopedGroups);

  return {
    baselineScanRoot,
    changesRequestedMode,
    clusterOrder,
    confirm,
    approveScope,
    getUnreviewedTasksInGroupCallback,
    groupClusters,
    handleBulkApproveGroup,
    handlePrintPreview,
    isBulkApproving,
    isReady,
    filterRegex,
    navigate,
    navigateToProject,
    requestChanges,
    reviewMode,
    scan,
    scanId,
    scanIsEditable,
    scanLifecycleStatus,
    scanMutators,
    scanNoteMutators,
    scanNotesByScanPartId,
    scanRoot,
    scopeApprovalStatus,
    scopedGroups,
    scopedTaskIdsByGroupId,
    startReview,
    submitForReview,
  };
};
